
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { addDays, format } from "date-fns";
import { tr } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Toaster } from "@/components/ui/toaster";

const ScheduleTrial = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedTime, setSelectedTime] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [coach, setCoach] = useState<any>(null);
  const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // First try to get coach from location state
    if (location.state?.coach) {
      setCoach(location.state.coach);
    } else {
      // If not in location state, try localStorage
      const storedCoach = JSON.parse(localStorage.getItem('selectedCoach') || '{}');
      if (!storedCoach.id) {
        navigate('/coaches');
        return;
      }
      setCoach(storedCoach);
    }

    // If no user details, redirect to user details page
    if (!userDetails.email || !userDetails.nameSurname || !userDetails.phone) {
      navigate('/user-details', { 
        state: { 
          returnTo: '/schedule-trial',
          coach: location.state?.coach || JSON.parse(localStorage.getItem('selectedCoach') || '{}')
        }
      });
    }
  }, [location.state, navigate, userDetails]);

  // Generate available time slots (9 AM to 6 PM)
  const timeSlots = Array.from({ length: 19 }, (_, i) => {
    const hour = Math.floor((i + 18) / 2);
    const minute = (i % 2) * 30;
    return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  });

  const handleSchedule = async () => {
    if (!selectedDate || !selectedTime || !coach) return;

    try {
      setIsSubmitting(true);
      const [hours, minutes] = selectedTime.split(':').map(Number);
      
      // Create a new date object and set it to the selected date and time in Istanbul timezone (GMT+3)
      const meetingDateTime = new Date(selectedDate);
      meetingDateTime.setHours(hours, minutes, 0, 0);

      // Convert to Istanbul timezone by adding 3 hours
      const istanbulOffset = 3 * 60 * 60 * 1000; // 3 hours in milliseconds
      const istanbulDateTime = new Date(meetingDateTime.getTime() + istanbulOffset);

      const { error } = await supabase
        .from('trial_sessions')
        .insert({
          coach_id: coach.id,
          email: userDetails.email,
          name_surname: userDetails.nameSurname,
          phone: userDetails.phone,
          meeting_date: istanbulDateTime.toISOString(),
        });

      if (error) {
        // Check if it's a trial limit error
        if (error.message.includes('Maximum number of free trials')) {
          toast({
            variant: "destructive",
            title: "Deneme Dersi Limiti",
            description: "Maksimum deneme dersi sayısına ulaştınız.",
          });
          setTimeout(() => {
            navigate('/coaches');
          }, 2000);
          return;
        }
        throw error;
      }

      toast({
        title: "Başarılı!",
        description: "Deneme dersi talebiniz alındı. Size en kısa sürede dönüş yapacağız.",
      });

      // Clear stored coach data before navigating
      localStorage.removeItem('selectedCoach');
      
      // Add a small delay to ensure the toast is visible before navigation
      setTimeout(() => {
        navigate('/');
      }, 3500);
      
    } catch (error: any) {
      console.error('Error scheduling trial:', error);
      toast({
        variant: "destructive",
        title: "Hata",
        description: "Bir hata oluştu. Lütfen tekrar deneyiniz.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const tomorrow = addDays(new Date(), 1);

  if (!userDetails.email) {
    return null; // Return null while redirecting
  }

  return (
    <div className="min-h-screen bg-background pt-24">
      <div className="container mx-auto px-4">
        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <CardTitle>Deneme Dersi Planla</CardTitle>
            <CardDescription>
              {coach?.name_surname} ile ücretsiz deneme dersi için uygun bir zaman seçin
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex flex-col sm:flex-row gap-6">
              <div className="flex-1">
                <h3 className="text-lg font-medium mb-3">Tarih Seçin</h3>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  fromDate={tomorrow}
                  locale={tr}
                  className="rounded-md border"
                />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium mb-3">Saat Seçin</h3>
                <Select onValueChange={setSelectedTime} value={selectedTime}>
                  <SelectTrigger>
                    <SelectValue placeholder="Bir saat seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((time) => (
                      <SelectItem key={time} value={time}>
                        {time}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {selectedDate && selectedTime && (
              <div className="bg-primary/5 p-4 rounded-lg">
                <p className="text-primary font-medium">
                  Seçilen Randevu: {format(selectedDate, 'd MMMM yyyy', { locale: tr })} - {selectedTime}
                </p>
              </div>
            )}

            <div className="flex justify-end">
              <Button 
                onClick={handleSchedule}
                disabled={!selectedDate || !selectedTime || isSubmitting}
              >
                {isSubmitting ? "Gönderiliyor..." : "Randevuyu Onayla"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
      <Toaster />
    </div>
  );
};

export default ScheduleTrial;
